/* eslint-disable no-unused-vars */
import 'aos/dist/aos.css'
import CookieConsent from './components/common/CookieConsent'
import React, { useCallback, useEffect, Suspense, lazy, useState } from 'react'
import './App.css'
import { useTranslation } from 'react-i18next'
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom'
import FranceFlag from './assets/svgs/flag-france.svg'
import GermanyFlag from './assets/svgs/flag-germany.svg'
import SpainFlag from './assets/svgs/flag-spain.svg'
import EngFlag from './assets/svgs/flag-uk.svg'
import ItalyFlag from './assets/svgs/flag-italy.svg'
import PortugalFlag from './assets/svgs/flag-portugal.svg'
import { getCookie, setCookie } from './utils/cookies'
import Navbar from './components/common/Navbar'
import ScrollToTop from './components/common/ScrollToTop'
import closeicon from "./assets/images/close_Icon.png"
import { useTheme } from './context/ThemeContext'

// ── Critical path: Home is eager (it IS the initial render)
import Home from './pages/Home'

// ── Sub-pages: all lazy — only download when navigated to
const Privacy = lazy(() => import('./pages/Privacy'))
const Eula = lazy(() => import('./pages/Eula'))
const ReturnPolicy = lazy(() => import('./pages/ReturnPolicy'))
const GettingStarted = lazy(() => import('./pages/GettingStarted'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'))
const ReferralProgramPage = lazy(() => import('./pages/ReferralProgramPage'))
const BlogsPage = lazy(() => import('./pages/BlogsPage'))
const PreSaleOffer = lazy(() => import('./pages/PreSaleOffer'))
const PrivacyStatement = lazy(() => import('./pages/PrivacyStatement'))
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'))
const Cookie = lazy(() => import('./pages/Cookie'))
const Conditions = lazy(() => import('./pages/Conditions'))
const Temps = lazy(() => import('./pages/Temps'))
const ThankYou = lazy(() => import('./components/common/ThankYou'))
const CoachPage = lazy(() => import('./pages/audiences/CoachPage'))

// ── Modal + SignUpForm: lazy — only needed on button click
const SignUpFormPopup = lazy(() => import('./components/forms/SignUpFormPopup'))
import { Modal as FlowbiteModal } from 'flowbite-react'

// ── Firebase analytics — init is driven by Home.jsx's eager firebase import;
//    logEvent is only called inside event handlers (not blocking)
import { logEvent } from 'firebase/analytics'
import { analytics } from './firebase'

export const languagePptions = [
    { value: 'en', label: 'EN', icon: EngFlag },
    { value: 'de', label: 'DE', icon: GermanyFlag },
    { value: 'fr', label: 'FR', icon: FranceFlag },
    { value: 'es', label: 'ES', icon: SpainFlag },
    { value: 'it', label: 'IT', icon: ItalyFlag },
    { value: 'pt', label: 'PT', icon: PortugalFlag },
]



function App() {
    const navigate = useNavigate()
    const { t, i18n } = useTranslation()
    const [activeLanguage, setActiveLanguage] = useState(languagePptions[0])
    const [showSignUpPopup, setShowSignUpPopup] = useState(false)
    const [signupPlatform, setSignupPlatform] = useState(null) // null | 'ios' | 'android'
    const [language, setLanguage] = useState('')
    const { isDarkMode, toggleTheme } = useTheme()
    const location = useLocation()
    // On the coaches page the right rail pins store badges at the bottom, so
    // lift the theme toggle above them to avoid overlap.
    const isCoachPage = location.pathname.replace(/\/+$/, '') === '/coaches'

    // Add this function to check URL parameters
    const checkURLParams = useCallback(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const signupParam = queryParams.get('signup');
        if (signupParam === 'true' || signupParam === 'ios' || signupParam === 'android') {
            setShowSignUpPopup(true);
            // Track which platform initiated the signup
            if (signupParam === 'ios') {
                setSignupPlatform('ios');
            } else if (signupParam === 'android') {
                setSignupPlatform('android');
            } else {
                setSignupPlatform(null);
            }
        }
    }, []);



    const handleRedirect = useCallback(
        (userLang) => {
            if (['en', 'de', 'fr', 'es'].includes(userLang)) {
                navigate(`/${userLang}`)
            } else {
                navigate(`/en`)
            }
        },
        [navigate]
    )

    // Ensure page is at top when signup popup opens
    useEffect(() => {
        if (showSignUpPopup) {
            // Use a microtask to ensure modal layout has mounted
            setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            }, 0)
        }
    }, [showSignUpPopup])
    // For tracking website visits from meta ad campaigns
    useEffect(() => {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');
        const utmContent = urlParams.get('utm_content');

        // If UTM parameters exist, log them to Firebase
        if (utmSource || utmMedium || utmCampaign) {

            logEvent(analytics, 'campaign_details', {
                campaign_source: utmSource || '(not set)',
                campaign_medium: utmMedium || '(not set)',
                campaign_name: utmCampaign || '(not set)',
                campaign_content: utmContent || '(not set)'
            });
        }
    }, []);

    useEffect(() => {
        // Check URL parameters when component mounts
        checkURLParams();

        var manuallySetLanguage = getCookie('user_language_preference')
        if (!manuallySetLanguage) {
            var FullUserLang = i18n.resolvedLanguage
            var userLang = FullUserLang.slice(0, 2)
            setCookie('user_language_preference', userLang, 30)
        } else {
            const activeLang = languagePptions.find(
                (lang) => lang.value === manuallySetLanguage
            )
            var htmlElement = document.querySelector('html')
            htmlElement.lang = activeLang.value
            setActiveLanguage(activeLang)
        }
    }, [activeLanguage, handleRedirect, i18n.resolvedLanguage, checkURLParams])

    // Add listener for route changes
    useEffect(() => {
        const handleRouteChange = () => {
            checkURLParams();
        };

        window.addEventListener('popstate', handleRouteChange);
        return () => {
            window.removeEventListener('popstate', handleRouteChange);
        };
    }, [checkURLParams]);

    useEffect(() => {
        const setVisible = () => {
            document.querySelector('.banner-wrapper')?.classList.add('visible');
        };

        if (window.innerWidth >= 768) {
            const timeout = setTimeout(setVisible, 3000);
            return () => clearTimeout(timeout);
        } else {
            const timeout = setTimeout(setVisible, 1000);
            return () => clearTimeout(timeout);
        }
    }, []);


    const handleSignUpClose = () => {
        setShowSignUpPopup(false)
        setSignupPlatform(null)
    }

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    return (
        <>
            {/* Theme Toggle Circle */}
            <button
                onClick={toggleTheme}
                className={`fixed ${isCoachPage ? 'xl:bottom-44 bottom-44' : 'xl:bottom-8 bottom-20'} right-5 z-[9999] w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 border border-white/10`}
                style={{
                    background: 'var(--card-bg)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
                    fontSize: '1.2rem'
                }}
                aria-label="Toggle light/dark mode"
            >
                {isDarkMode ? '🌙' : '☀️'}
            </button>
            <div className={showSignUpPopup ? 'overlay-blur' : ''}>
                <ScrollToTop />
                {/* Coaches page carries its own fixed side rail / bottom bar — suppress the global nav there. */}
                {!isCoachPage && <Navbar setShowSignUpPopup={setShowSignUpPopup} />}
                {showSignUpPopup && (
                    <Suspense fallback={null}>
                        <FlowbiteModal
                            show={showSignUpPopup}
                            onClose={handleSignUpClose}
                            className="animate-fade-in-modal signup_pop max-h-[100dvh] mt-0 md:mt-auto z-[999]"
                            popup
                            dismissible
                            closable
                        >
                            <FlowbiteModal.Header className="header-background"  >
                                <span className='form-title'>{t('modals.signup_title')}</span>
                            </FlowbiteModal.Header>
                            <FlowbiteModal.Body className="p-0 max-h-[100dvh] overflow-y-auto">
                                <SignUpFormPopup
                                    showSignUpPopup={showSignUpPopup}
                                    language={language}
                                    onClose={handleSignUpClose}
                                    signupPlatform={signupPlatform}
                                />
                            </FlowbiteModal.Body>
                        </FlowbiteModal>
                    </Suspense>
                )}
                <main className="app">
                    <Suspense fallback={<div>Loading...</div>}>

                        <Routes>
                            <Route path="/temp" element={<Temps />} />
                            <Route path="/coaches" element={<CoachPage />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/eula" element={<Eula />} />
                            <Route path="/cookie" element={<Cookie />} />
                            <Route path="/conditions" element={<Conditions />} />
                            <Route path="/refunds" element={<ReturnPolicy />} />
                            <Route path="/how-to-use" element={<GettingStarted />} />
                            <Route path="/features" element={<FeaturesPage />} />
                            <Route path="/referral-program" element={<ReferralProgramPage />} />
                            <Route path="/presale" element={<PreSaleOffer />} />
                            <Route path="/public_video_privacy" element={<PrivacyStatement />} />
                            <Route path={`/blogs/${activeLanguage.value}`} element={<BlogsPage />} />
                            <Route path="/unsubscribe" element={<Unsubscribe />} />
                            <Route path="/pricing" element={<PricingPage />} />
                            <Route
                                path="/*"
                                element={
                                    <Home
                                        showSignUpPopup={showSignUpPopup}
                                        setShowSignUpPopup={setShowSignUpPopup}
                                    />
                                }
                            />
                        </Routes>
                    </Suspense>

                    <div className="banner-wrapper">
                        <div className="banner-box animated-border z-[9999999] p-2 pr-4 rounded-xl">
                            <span className="font-bold text-[16px] inline-block free-hrs">
                                <span className="block whitespace-nowrap lift-up word-1 text-banner-primary">{t('meta.home.freeBanner.line1')}</span>
                                <span className="block whitespace-nowrap lift-up word-2 text-banner-secondary">{t('meta.home.freeBanner.line2')}</span>
                            </span>
                        </div>
                    </div>


                </main>
            </div>

            {/* Cookie Consent Banner */}
            <CookieConsent />
        </>
    )
}

export default App